import React, { useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import LocalizedDatePicker from "components/Common/LocalizedDatePicker"
import Paginations from "components/Common/Paginations"
import { saGetAllSubscriptions } from "store/actions/superAdminAction"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import isPermissionEnabled from "common/isPermissionEnabled"
import { translatePaymentGateway, translateStatusLabel } from "utils/statusLabels"
import { getCurrencyLabel } from "utils/currency"

const STATUS_COLORS = {
  active: "success",
  expired: "danger",
  cancelled: "secondary",
  inactive: "warning",
  suspended: "warning",
}

const SubscriptionsList = () => {
  const { t } = useTranslation()
  document.title = t("All Subscriptions | Super Admin")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const pageNumber = parseInt(new URLSearchParams(location.search).get("page")) || 1

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewBilling = isPermissionEnabled(
    userRole,
    106,
    "sa_billing_1",
    accountType,
    accountScope
  )

  const { subscriptionsLoading, subscriptions, subscriptionsTotalCount, subscriptionsTotalPages } =
    useSelector(state => state.SuperAdmin)

  const [statusFilter, setStatusFilter] = useState("")
  const [planFilter, setPlanFilter] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!canViewBilling) {
      navigateToAccessibleRoute(navigate)
      return
    }
    dispatch(
      saGetAllSubscriptions({
        page: pageNumber,
        limit: 10,
        status: statusFilter || undefined,
        planName: planFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
    )
  }, [
    dispatch,
    pageNumber,
    statusFilter,
    planFilter,
    dateFrom,
    dateTo,
    isSuperAdminSession,
    canViewBilling,
    navigate,
  ])

  const handlePageChange = page => {
    const params = new URLSearchParams(location.search)
    params.set("page", page.toString())
    navigate(`${location.pathname}?${params.toString()}`)
  }

  const handleReset = () => {
    setStatusFilter("")
    setPlanFilter("")
    setDateFrom("")
    setDateTo("")
    navigate(`${location.pathname}?page=1`)
    dispatch(saGetAllSubscriptions({ page: 1, limit: 10 }))
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <h4 className="mb-1">{t("All Subscriptions")}</h4>
          <p className="text-muted mb-3">
            {t("Cross-tenant subscription overview")}
          </p>

          <Card>
            <CardBody>
              {/* Filters */}
              <Row className="mb-3 g-2">
                <Col md={3}>
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="">{t("All Statuses")}</option>
                    <option value="active">{t("Active")}</option>
                    <option value="expired">{t("Expired")}</option>
                    <option value="cancelled">{t("Cancelled")}</option>
                    <option value="inactive">{t("Inactive")}</option>
                    <option value="suspended">{t("Suspended")}</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    placeholder={t("Filter by plan name")}
                    value={planFilter}
                    onChange={e => setPlanFilter(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <LocalizedDatePicker
                    value={dateFrom}
                    onChange={setDateFrom}
                    placeholder={t("From date")}
                  />
                </Col>
                <Col md={3}>
                  <LocalizedDatePicker
                    value={dateTo}
                    onChange={setDateTo}
                    placeholder={t("To date")}
                  />
                </Col>
                <Col md={12} className="d-flex justify-content-end">
                  <Button
                    color="light"
                    className="d-flex align-items-center"
                    onClick={handleReset}
                    disabled={subscriptionsLoading}
                  >
                    <i className="mdi mdi-refresh me-1" style={{ fontSize: 18 }} />
                    {t("Reset")}
                  </Button>
                </Col>
              </Row>

              {subscriptionsLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {t("No subscriptions found.")}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("Tenant")}</th>
                          <th>{t("Plan")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Started")}</th>
                          <th>{t("Expires")}</th>
                          <th>{t("Amount")}</th>
                          <th>{t("Payment Gateway")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.map((sub, idx) => (
                          <tr key={sub._id || idx}>
                            <td className="fw-medium">
                              {sub.tenant?.name || sub.tenantName || "—"}
                            </td>
                            <td>{sub.plan?.name || sub.planName || "—"}</td>
                            <td>
                              <Badge
                                color={STATUS_COLORS[sub.status] || "secondary"}
                                className="text-capitalize"
                              >
                                {translateStatusLabel(t, sub.status)}
                              </Badge>
                            </td>
                            <td>
                              {sub.startedAt
                                ? new Date(sub.startedAt).toLocaleDateString()
                                : "—"}
                            </td>
                            <td>
                              {sub.expiresAt
                                ? new Date(sub.expiresAt).toLocaleDateString()
                                : "—"}
                            </td>
                            <td>
                              {getCurrencyLabel(sub.currency || "SAR")}{" "}
                              {Number(sub.amount || 0).toFixed(2)}
                            </td>
                            <td>
                              {translatePaymentGateway(t, sub.paymentGateway)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Paginations
                    currentPage={pageNumber}
                    totalPages={subscriptionsTotalPages}
                    onPageChange={handlePageChange}
                    totalCount={subscriptionsTotalCount}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default SubscriptionsList
